import { Dialog } from '@/components/Dialog';
import { CheckedIcon, UncheckedIcon } from '@/components/Icons';
import { updateUserName } from '@/service/user';
import { cn } from '@/utils';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useSessionStore } from './useSessionStore';

interface FormValues {
  artistName: string;
  agreeToTerms: boolean;
}

let dialogInstance = false;

export const useUserNameDialog = () => {
  const showUsernameDialog = (onFinish?: () => void) => {
    if (dialogInstance) return;
    dialogInstance = true;

    Dialog.info({
      closeClassName: 'hidden',
      overlayClassName: 'bg-white/60',
      canNotCloseOnMask: true,
      dialogContentClassName: 'w-[340px] h-[464px] bg-transparent outline-none rounded-xl',
      contentJSX: ({ onClose }) => {
        const { fetchData } = useSessionStore();

        const { handleSubmit, setValue, watch, register } = useForm<FormValues>({
          defaultValues: {
            artistName: '',
            agreeToTerms: false,
          },
        });
        const agreeToTerms = watch('agreeToTerms');
        const artistName = watch('artistName');
        const onSubmit = async (data: FormValues) => {
          dialogInstance = false;
          try {
            await updateUserName({ name: data.artistName });
            fetchData();
            toast('Profile Updated!');
            onClose();
          } catch (error) {
            onClose();
          }
          onFinish?.();
        };
        return (
          <form onSubmit={handleSubmit(onSubmit)} className=" w-[340px] h-[464px] bg-jen-background-primary rounded-xl shadow-lg ">
            <div className="p-[16px] flex flex-col gap-[16px] border-b border-jen-border">
              <div className="flex justify-center items-center bg-jen-content-primary w-[112px] h-[16px] text-jen-background-primary rounded-sm text-[12px] font-pressura-mono">
                BETA RELEASE
              </div>
              <div className="text-jen-content-primary text-[24px] leading-[24px]">Welcome to Jen</div>
              <div className=" text-jen-size-heading-04 leading-5 text-jen-interactive-secondary">
                Jen aced her ethical-training course and has just completed her first level of high-fidelity generative music. She's eager
                to start co-producing with you. But, being a multi-instrumentalist of every genre is hard. Your feedback is invaluable and
                will help her improve, so have fun and remember, she's just getting started.
              </div>
            </div>
            <div className="p-[16px]">
              <div className="flex flex-col gap-[16px]">
                <div className="flex flex-col gap-[8px]">
                  <div className=" text-jen-size-heading-04 text-jen-interactive-secondary">Your artist name</div>
                  <input
                    {...register('artistName', { required: true })}
                    placeholder="Type name"
                    className="outline-none rounded py-[8px] px-[16px] w-full h-[40px] text-jen-content-primary text-[16px] border border-jen-border"
                  />
                </div>
                <div className=" flex gap-[12px] items-center h-[20px]">
                  <Checkbox id={'beta-check'} onChange={(checked) => setValue('agreeToTerms', checked)} />
                  <label htmlFor="beta-check">
                    I agree to the{' '}
                    <span className=" underline">
                      <a target="__blank" href="https://www.jenmusic.ai/terms-of-use">
                        terms and conditions
                      </a>{' '}
                    </span>
                  </label>
                </div>
                <button
                  type="submit"
                  className={cn(
                    ' w-full bg-jen-interactive-primary rounded-lg h-[48px] flex justify-center items-center text-jen-content-primary-contrast text-[16px] opacity-100 ',
                    {
                      'opacity-20 cursor-not-allowed': !agreeToTerms || !artistName,
                      'hover:opacity-80 cursor-pointer': !!agreeToTerms && !!artistName,
                    },
                  )}
                >
                  Get Started
                </button>
              </div>
            </div>
          </form>
        );
      },
    });
  };
  return { showUsernameDialog };
};

interface CheckboxProps {
  id?: string;
  onChange: (checked: boolean) => void;
}

const Checkbox: React.FC<CheckboxProps> = ({ id, onChange }) => {
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    onChange(checked);
  }, [checked]);
  return (
    <div onClick={() => setChecked(!checked)} className="cursor-pointer">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="hidden" />
      {checked ? <CheckedIcon /> : <UncheckedIcon />}
    </div>
  );
};
